import { type FC, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import {
  DataId,
  DataRow,
  Spinner,
  Table,
  useSimpleTableDataSource,
} from "capitalrx-components-2.0/lib/general";
import { Stack } from "capitalrx-components-2.0/lib/layout";

import { getPaymentPreferenceListAPI } from "@/api";
import { ProviderManagement } from "@/api/interfaces";
import { ProviderStatus, PAYMENT_TYPE_LABELS, REMITTANCE_TYPE_LABELS } from "@/constants";
import { AuthenticationContext } from "@/context";
import { getThemeURL } from "@/utils";

import { StatusTag } from "./StatusTag";

type NetworkTableProps = {
  provider: ProviderManagement.ProviderSchema;
  networks: ProviderManagement.ProviderNetworkListSearchResponse;
  config?: ProviderManagement.ProviderNetworkConfigListSearchResponse;
};

export const findProviderInfoByPaymentId = (
  paymentObject: ProviderManagement.PaymentPreferenceSchema,
  networkConfigObject: ProviderManagement.ProviderNetworkConfigListSearchResponse,
): { provider_id: number; provider_network_id: number; payment_info_id: number } | null => {
  const paymentId = paymentObject?.id;
  if (!paymentId || !networkConfigObject?.results?.length) {
    return null;
  }

  const matchingConfig = networkConfigObject.results.find(
    (item) => item.provider_network_configuration?.payment_info_id === paymentId,
  );

  if (!matchingConfig) return null;

  const { provider_id, provider_network_id, payment_info_id } = matchingConfig.provider_network_configuration;

  return { provider_id, provider_network_id, payment_info_id };
};

export const NetworkTable: FC<NetworkTableProps> = ({ provider, networks, config }) => {
  const providerId = provider?.id;
  const navigate = useNavigate();
  const { getAuthToken } = useContext(AuthenticationContext);

  const [paymentResults, setPaymentResults] = useState<ProviderManagement.PaymentPreferenceListSearchResponseResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchPayments = async () => {
      if (!providerId) return;
      setIsLoading(true);

      try {
        const requestPayload: ProviderManagement.PaymentPreferenceListSearchRequest = {
          provider_id: providerId,
          in_network: true,
        };

        const response = await getPaymentPreferenceListAPI(requestPayload, getAuthToken());
        setPaymentResults(response.results || []);
      } catch (error) {
        console.error("Failed to fetch payment preferences:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPayments();
  }, [providerId, getAuthToken()]);

  const networkMap = useMemo(() => {
    if (!networks?.results || !config?.results) return {};

    const networkIdToName = networks.results.reduce(
      (map, networkResult) => {
        const network = networkResult.provider_network;
        map[network.id] = network.name;
        return map;
      },
      {} as Record<number, string>,
    );

    return config.results.reduce(
      (map, configResult) => {
        const config = configResult.provider_network_configuration;
        const paymentInfoId = config.payment_info_id;
        const networkId = config.provider_network_id;

        const networkName = networkIdToName[networkId];
        if (networkName) {
          map[paymentInfoId] = networkName;
        }

        return map;
      },
      {} as Record<number, string>,
    );
  }, [networks, config]);

  const rows: ReadonlyArray<DataRow<ProviderManagement.PaymentPreferenceListSearchResponseResult>> = useMemo(() => {
    return paymentResults.map((item) => ({
      id: String(item.provider_payment_info.id),
      data: item,
    }));
  }, [paymentResults]);

  const dataSource = useSimpleTableDataSource(rows, {
    pageSize: 10,
  });

  const handleRowClick = (payment_info_id: DataId): void => {
    const baseUrl = getThemeURL("ACCOUNT");

    const data = dataSource.lookup(payment_info_id);
    const result = findProviderInfoByPaymentId(data?.provider_payment_info, config);
    const params: Record<string, string | number | undefined> = {
      name: networkMap[payment_info_id],
    };

    const encodedParams = new URLSearchParams();
    for (const key in params) {
      if (params[key]) {
        encodedParams.append(key, String(params[key]));
      }
    }

    navigate(`${baseUrl}/network/${result?.provider_network_id}?${encodedParams.toString()}`);
  };

  return (
    <>
      {isLoading && <Stack alignment="center"><Spinner size="large" /></Stack>}
      {!isLoading && (
        <Table dataSource={dataSource} testId="provider_network_list_table" onRowClick={handleRowClick}>
          <Table.Custom name="provider_payment_info.name" title="Network" initialWidth="span">
            {(value: unknown, _id: DataId, row: DataRow<ProviderManagement.PaymentPreferenceListSearchResponseResult>) => {
                const paymentInfoId = row.data.provider_payment_info.id;
                return networkMap[paymentInfoId] || "--";
              }}
          </Table.Custom>

          <Table.Custom name="provider_payment_info.payment_type" title="Payment Type" initialWidth="fit">
            {(value: unknown) => {
              const paymentType = value as string;
              return PAYMENT_TYPE_LABELS[paymentType as ProviderManagement.PaymentTypeValue] || paymentType;
            }}
          </Table.Custom>

          <Table.Custom name="provider_payment_info.remittance_type" title="Remittance Type" initialWidth="fit">
            {(value: unknown) => {
              const remittanceType = value as string;
              return REMITTANCE_TYPE_LABELS[remittanceType as ProviderManagement.RemittanceTypeValue] || remittanceType;
            }}
          </Table.Custom>

          <Table.Custom name="provider_payment_info.network_enrollment_status" title="Status" initialWidth="fit">
            {(value: unknown, _id: DataId, row: DataRow<ProviderManagement.PaymentPreferenceListSearchResponseResult>) => {
              const status = value as ProviderStatus;
              const paymentType = row.data.provider_payment_info.payment_type;
              return <StatusTag status={status} paymentType={paymentType} />;
            }}
          </Table.Custom>
        </Table>
      )}
    </>

  );
};
